/**
 * Sorting a list by one column: numbers as numbers, words with their numbers
 * in order ("Item 2" before "Item 10"), and rows with nothing in that column
 * always last, whichever way the list is sorted.
 */

export type SortDirection = 'asc' | 'desc';

export type SortKey = string | number | null | undefined;

const words = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/** A cell's raw value as something that can be put in order. */
export function sortKey(value: unknown): SortKey {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(String).join(', ') || null;
  if (typeof value === 'object') {
    const named = value as { name?: unknown; display_name?: unknown };
    const name = named.name ?? named.display_name;
    return typeof name === 'string' && name ? name : null;
  }
  // A symbol, a function: nothing a person reads in a cell.
  return null;
}

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return words.compare(String(a), String(b));
}

/** A sorted copy; the order of equal rows is kept. */
export function sortRows<Row>(
  rows: readonly Row[],
  key: (row: Row) => SortKey,
  direction: SortDirection,
): Row[] {
  const sign = direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: key(row) }))
    .sort((x, y) => {
      const xEmpty = x.value === null || x.value === undefined;
      const yEmpty = y.value === null || y.value === undefined;
      if (xEmpty || yEmpty) {
        if (xEmpty && yEmpty) return x.index - y.index;
        return xEmpty ? 1 : -1;
      }
      return (
        sign *
          compare(x.value as string | number, y.value as string | number) ||
        x.index - y.index
      );
    })
    .map((entry) => entry.row);
}
